import {useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore} from 'react'

const ACCEPTED_SCHEMES = [
  {scheme: 'ghapp', prefix: 'ghapp://'},
  {scheme: 'github-app', prefix: 'github-app://'},
  {scheme: 'gh', prefix: 'gh://'},
] as const

export type AcceptedDeepLinkScheme = (typeof ACCEPTED_SCHEMES)[number]['scheme']
const GITHUB_HOME_URL = 'https://github.com/'
const GITHUB_HOST = 'github.com'
const LAUNCHER_QUERY_PARAMS = new Set(['entry_point'])
export const DEFAULT_DEEP_LINK = 'ghapp://'
export const LAUNCH_DELAY_MS = 500
export const EXPIRED_DELAY_MS = 4000

export type LaunchDisplayState = 'waiting' | 'success' | 'expired'
export type LaunchDeepLink = (deepLink: string) => void
export type LaunchTrigger = 'auto' | 'manual' | 'retry'
export type ContinueLinkKind = 'continue' | 'goBack'

export interface ContinueLink {
  href: string
  kind: ContinueLinkKind
}

interface UseDeepLinkLaunchOptions {
  target: string
  displayState?: LaunchDisplayState
  launchDeepLink: LaunchDeepLink
  shouldAutoLaunch?: boolean
  launchDelayMs?: number
  onAutoLaunchAttempt?: () => void
  onManualLaunchAttempt?: (trigger: Exclude<LaunchTrigger, 'auto'>, state: LaunchDisplayState) => void
  onLaunchExpired?: () => void
}

export function decodeOpenTarget(search: string) {
  return new URLSearchParams(search).get('open')
}

export function isAcceptedDeepLink(target: string) {
  return getAcceptedDeepLinkScheme(target) !== undefined
}

export function isValidAcceptedDeepLink(target: string) {
  if (!isAcceptedDeepLink(target)) return false

  try {
    new URL(target, GITHUB_HOME_URL)
    return true
  } catch {
    return false
  }
}

export function getAcceptedDeepLinkScheme(target: string): AcceptedDeepLinkScheme | undefined {
  const normalizedTarget = target.toLowerCase()
  return ACCEPTED_SCHEMES.find(({prefix}) => normalizedTarget.startsWith(prefix))?.scheme
}

export function stripLauncherQueryParams(target: string) {
  const hashStart = target.indexOf('#')
  const targetBeforeHash = hashStart === -1 ? target : target.slice(0, hashStart)
  const targetHash = hashStart === -1 ? '' : target.slice(hashStart)
  const queryStart = targetBeforeHash.indexOf('?')

  if (queryStart === -1) return target

  const targetBeforeQuery = targetBeforeHash.slice(0, queryStart)
  const targetQuery = targetBeforeHash.slice(queryStart + 1)
  const retainedParams = targetQuery.split('&').filter(param => !LAUNCHER_QUERY_PARAMS.has(getQueryParamName(param)))

  const retainedQuery = retainedParams.length > 0 ? `?${retainedParams.join('&')}` : ''

  return `${targetBeforeQuery}${retainedQuery}${targetHash}`
}

function getQueryParamName(param: string) {
  const valueStart = param.indexOf('=')
  const rawName = valueStart === -1 ? param : param.slice(0, valueStart)

  try {
    return decodeURIComponent(rawName.replace(/\+/g, ' '))
  } catch {
    return rawName
  }
}

export function resolveLaunchTarget(target: string | null) {
  return target && isValidAcceptedDeepLink(target) ? stripLauncherQueryParams(target) : DEFAULT_DEEP_LINK
}

export function getGitHubContinueUrl(target: string | null) {
  if (!target || !isAcceptedDeepLink(target)) return undefined

  try {
    const targetUrl = new URL(target, GITHUB_HOME_URL)
    if (targetUrl.hostname.toLowerCase() !== GITHUB_HOST) return undefined

    return `${GITHUB_HOME_URL}${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`
  } catch {
    return undefined
  }
}

export function resolveContinueLink(target: string | null, referrer: string): ContinueLink {
  const githubContinueUrl = getGitHubContinueUrl(target)
  if (githubContinueUrl) {
    return {
      href: githubContinueUrl,
      kind: 'continue',
    }
  }

  return {
    href: referrer || GITHUB_HOME_URL,
    kind: 'goBack',
  }
}

function getBrowserReferrer() {
  return typeof document === 'undefined' ? '' : document.referrer
}

function getServerReferrer() {
  return ''
}

function subscribeToReferrer() {
  return () => {}
}

export function useContinueLink(target: string | null) {
  const referrer = useSyncExternalStore(subscribeToReferrer, getBrowserReferrer, getServerReferrer)
  return useMemo(() => resolveContinueLink(target, referrer), [target, referrer])
}

export function defaultLaunchDeepLink(deepLink: string) {
  if (typeof window === 'undefined') return
  window.location.href = deepLink
}

export function useDeepLinkLaunch({
  target,
  displayState,
  launchDeepLink,
  shouldAutoLaunch = true,
  launchDelayMs = LAUNCH_DELAY_MS,
  onAutoLaunchAttempt,
  onManualLaunchAttempt,
  onLaunchExpired,
}: UseDeepLinkLaunchOptions) {
  const [status, setStatus] = useState<LaunchDisplayState>('waiting')
  const [hasExpired, setHasExpired] = useState(false)
  const cleanupRef = useRef<(() => void) | undefined>(undefined)
  const onAutoLaunchAttemptRef = useRef(onAutoLaunchAttempt)
  const onManualLaunchAttemptRef = useRef(onManualLaunchAttempt)
  const onLaunchExpiredRef = useRef(onLaunchExpired)

  useEffect(() => {
    onAutoLaunchAttemptRef.current = onAutoLaunchAttempt
  }, [onAutoLaunchAttempt])

  useEffect(() => {
    onManualLaunchAttemptRef.current = onManualLaunchAttempt
  }, [onManualLaunchAttempt])

  useEffect(() => {
    onLaunchExpiredRef.current = onLaunchExpired
  }, [onLaunchExpired])

  const attemptLaunch = useCallback(
    (trigger: LaunchTrigger) => {
      cleanupRef.current?.()

      let launchTimer: number | undefined
      const launch = () => {
        if (trigger === 'auto') {
          onAutoLaunchAttemptRef.current?.()
        }

        launchDeepLink(target)
      }

      const fallbackTimer = window.setTimeout(() => {
        removeSuccessListeners()
        setStatus('expired')
        setHasExpired(true)
        onLaunchExpiredRef.current?.()
      }, EXPIRED_DELAY_MS)

      function removeSuccessListeners() {
        window.removeEventListener('pagehide', handleLaunchSuccessSignal)
        document.removeEventListener('visibilitychange', handleVisibilityChange)
      }

      function handleLaunchSuccessSignal() {
        window.clearTimeout(fallbackTimer)
        removeSuccessListeners()
        setStatus('success')
      }

      function handleVisibilityChange() {
        if (document.visibilityState === 'hidden') {
          handleLaunchSuccessSignal()
        }
      }

      // Browser confirmation dialogs can blur the page before the app opens.
      // Only page-hidden signals should stop the visible fallback countdown.
      window.addEventListener('pagehide', handleLaunchSuccessSignal)
      document.addEventListener('visibilitychange', handleVisibilityChange)

      if (launchDelayMs === 0) {
        launch()
      } else {
        launchTimer = window.setTimeout(launch, launchDelayMs)
      }

      const cleanup = () => {
        if (launchTimer !== undefined) {
          window.clearTimeout(launchTimer)
        }
        window.clearTimeout(fallbackTimer)
        removeSuccessListeners()
      }

      cleanupRef.current = cleanup
      return cleanup
    },
    [launchDeepLink, launchDelayMs, target],
  )

  const visibleState = displayState ?? status

  const handleRetry = useCallback(
    (trigger: Exclude<LaunchTrigger, 'auto'> = 'retry') => {
      onManualLaunchAttemptRef.current?.(trigger, visibleState)
      setStatus('waiting')
      attemptLaunch(trigger)
    },
    [attemptLaunch, visibleState],
  )

  const resetLaunch = useCallback(() => {
    cleanupRef.current?.()
    cleanupRef.current = undefined
    setStatus('waiting')
    setHasExpired(false)
  }, [])

  useEffect(() => {
    if (!shouldAutoLaunch) return

    attemptLaunch('auto')
    return () => cleanupRef.current?.()
  }, [attemptLaunch, shouldAutoLaunch])

  useEffect(() => {
    return () => cleanupRef.current?.()
  }, [target])

  return {
    visibleState,
    isExpired: visibleState === 'expired',
    useExpiredActionAffordance: hasExpired || visibleState === 'expired',
    handleRetry,
    resetLaunch,
  }
}
